import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FreshnessBadge } from "@/components/breakout/FreshnessBadge";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import InsightsIcon from "@mui/icons-material/Insights";
import PieChartIcon from "@mui/icons-material/PieChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import portfolioService, { type PortfolioSearchParams } from "@/services/portfolioService";
import stockMarketService from "@/services/stockMarketService";
import volumeObvBreakoutService from "@/services/volumeObvBreakoutService";
import type {
  BreakoutStatus,
  PortfolioConcentration,
  PortfolioHoldingRow,
  PortfolioMarketSignal,
  PortfolioPerformance,
  PortfolioSortField,
  PortfolioSummary,
  SectorBreakdownRow,
  SortOrder,
  StockMaster,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { exportToCsv } from "@/utils/csv";
import StockResearchDialog from "@/components/StockResearchDialog";
import SummaryMetricCard from "@/pages/options-dashboard/SummaryMetricCard";
import PortfolioHoldingDetailDialog from "@/pages/portfolio/PortfolioHoldingDetailDialog";

// Persists the user's chosen sort column/direction across page refreshes
// (requirement: "Preserve the selected sort after page refresh").
const SORT_STORAGE_KEY = "tekinvest.portfolioTracker.sort";

interface StoredSort {
  field: PortfolioSortField;
  order: SortOrder;
}

function loadStoredSort(): StoredSort | null {
  try {
    const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSort;
    if (!parsed.field || (parsed.order !== "asc" && parsed.order !== "desc")) return null;
    return parsed;
  } catch {
    return null;
  }
}

interface SortableColumn {
  field: PortfolioSortField;
  label: string;
  align?: "left" | "right";
}

// Column order matches the table's existing visual layout exactly — only
// the header cells themselves change (plain text -> clickable
// TableSortLabel), nothing about column order/content/layout.
// Portfolio Intelligence Phase 1: "Target Price" is inserted right after
// "Current Price", per spec. Not independently sortable server-side (the
// backend's `_SORTABLE_FIELDS` doesn't include it — it's a derived,
// per-holding research figure, not a stored/joined column worth adding a
// 10th sort key for) — the header renders as plain text, not a
// `TableSortLabel`, same convention as the "Actions" column.
const SORTABLE_COLUMNS: SortableColumn[] = [
  { field: "symbol", label: "Stock Symbol" },
  { field: "quantity", label: "Quantity", align: "right" },
  { field: "average_buy_price", label: "Average Buy Price", align: "right" },
  { field: "current_price", label: "Current Price", align: "right" },
  { field: "breakout_score", label: "Breakout Score", align: "right" },
  // UI review (2026-10-03): "Status" (raw breakout label, e.g. "Strong
  // Breakout") and "Portfolio Status" (the same band relabeled as a zone,
  // e.g. "Accumulate Zone") were two adjacent columns showing the identical
  // underlying band twice. Collapsed into one "Zone" column — sortable via
  // the same `portfolio_status` field (produces the same row order as
  // sorting by `status`, since the zone label is a pure 1:1 relabeling) —
  // with the raw Status label still available via a tooltip on the chip,
  // so no information is actually lost, just de-duplicated on screen.
  { field: "portfolio_status", label: "Zone" },
  { field: "status_change", label: "Status Change" },
  { field: "last_updated", label: "Last Updated" },
];

// Stock Scoring Standardization (2026-09-13): this page now reads the
// canonical Breakout Score Engine V2 (`BreakoutStatus`, 4 unidirectional
// bands) instead of Market Scanner's 5-band bullish/bearish `ScannerSignal`
// — same palette convention `MarketOpportunitiesPage`'s
// `BREAKOUT_STATUS_COLORS` uses (dark green = strongest confirmation, grey
// = nothing confirmed), duplicated locally rather than shared across pages
// per this app's existing per-page-palette convention.
const STATUS_COLORS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "#1B5E20",
  BREAKOUT_CANDIDATE: "#2E7D32",
  DEVELOPING_BREAKOUT: "#F9A825",
  NO_CONFIRMED_BREAKOUT: "#9E9E9E",
  INSUFFICIENT_DATA: "#5C6BC0",
  FAILED_BREAKOUT: "#C62828",
};

const STATUS_LABELS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "Strong Breakout",
  BREAKOUT_CANDIDATE: "Breakout Candidate",
  DEVELOPING_BREAKOUT: "Developing Breakout",
  NO_CONFIRMED_BREAKOUT: "No Confirmed Breakout",
  INSUFFICIENT_DATA: "Insufficient Data",
  FAILED_BREAKOUT: "Failed Breakout",
};

// Portfolio Intelligence Phase 1: the SAME 4 `BreakoutStatus` bands,
// relabeled to "zone" wording — see backend
// `PortfolioQueryService.PORTFOLIO_STATUS_LABELS` for the full rename
// rationale (this is a pure label mirror, not a second source of truth —
// the underlying band membership always comes from the row's own
// `status`/`portfolio_status` fields returned by the API).
const ZONE_LABEL_FOR_STATUS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "Accumulate Zone",
  BREAKOUT_CANDIDATE: "Hold Zone",
  DEVELOPING_BREAKOUT: "Watch Zone",
  NO_CONFIRMED_BREAKOUT: "Review Zone",
  INSUFFICIENT_DATA: "Data Unavailable",
  FAILED_BREAKOUT: "Review Zone",
};

// Portfolio Status zone chip palette — success/warning/error-ish tones
// consistent with this app's existing MUI theme conventions (mirrors the
// success/warning/error semantics `STATUS_COLORS` above already uses for
// the underlying breakout bands, just with the app's default MUI palette
// colors instead of the breakout engine's own dark-green/amber/grey scale).
const ZONE_COLORS: Record<string, string> = {
  "Accumulate Zone": "#2E7D32", // success.dark
  "Hold Zone": "#0288D1", // info.main
  "Watch Zone": "#ED6C02", // warning.main
  "Review Zone": "#D32F2F", // error.main
};

function PortfolioStatusChip({ label }: { label?: string | null }) {
  if (!label) return <Chip label="Not scored yet" size="small" variant="outlined" />;
  const color = ZONE_COLORS[label];
  return (
    <Chip
      label={label}
      size="small"
      sx={color ? { bgcolor: color, color: "#fff", fontWeight: 600 } : undefined}
      variant={color ? "filled" : "outlined"}
    />
  );
}

// Near-target window mirrors the backend's own (see
// `PortfolioQueryService._NEAR_TARGET_LOW_PCT`/`_HIGH_PCT`) — used both for
// the action-column priority rule and the detail dialog's "Approaching
// Target" state.
const NEAR_TARGET_LOW_PCT = -5;
const NEAR_TARGET_HIGH_PCT = 5;

/**
 * Action-column priority rule (documented per spec item 3):
 *   1. Target known AND within +/-5% of it -> "Review Target" (highest
 *      priority — a price-proximity event overrides the status-based label).
 *   2. Otherwise, by `portfolio_status`:
 *        Accumulate Zone -> "Review Accumulation"
 *        Hold Zone       -> "Review Holding"
 *        Watch Zone      -> "Review Trend"
 *        Review Zone     -> "Review Trend" (same as Watch Zone — both are
 *          "look closer, nothing confirmed/weak" states; a separate label
 *          wasn't judged to add meaningfully in either direction).
 *   3. Not yet scored (`portfolio_status` unknown) -> "View Details" (falls
 *      through to the generic, always-available action).
 * This is ONLY a tooltip/label on the "View Details" button — every path
 * opens the SAME detail dialog; there is no separate order-placement action.
 */
function actionLabel(row: PortfolioHoldingRow): string {
  if (
    row.target_upside_pct !== null &&
    row.target_upside_pct !== undefined &&
    row.target_upside_pct >= NEAR_TARGET_LOW_PCT &&
    row.target_upside_pct <= NEAR_TARGET_HIGH_PCT
  ) {
    return "Review Target";
  }
  switch (row.portfolio_status) {
    case "Accumulate Zone":
      return "Review Accumulation";
    case "Hold Zone":
      return "Review Holding";
    case "Watch Zone":
    case "Review Zone":
      return "Review Trend";
    default:
      return "View Details";
  }
}

const DISCLAIMER =
  "TekInvest provides market information and analytical signals generated using predefined technical models. " +
  "The information displayed is for educational and research purposes only and should not be considered as " +
  "investment advice, a recommendation or a solicitation to buy or sell any security. Users should conduct " +
  "their own research or consult a SEBI-registered investment adviser or research analyst before making " +
  "investment decisions.";

function num(value: string | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// Signed percentage display used by Sector Momentum, Portfolio Performance
// (Phase 1B) and the Target Price column — fixed to 2 decimals always (UI
// review 2026-10-03: this used to print the raw backend float verbatim,
// e.g. "+1%" vs "+16.73%" vs "+11.1%", with inconsistent precision purely as
// an artifact of whatever trailing zeros Python's `round()` happened to
// drop). "—" (never "0%"/fabricated) when the backend couldn't compute a
// value for that period (not enough price history).
function signedPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function pctColor(value: number | null | undefined): "success.main" | "error.main" | "text.secondary" {
  if (value === null || value === undefined) return "text.secondary";
  return value >= 0 ? "success.main" : "error.main";
}

function StatusChangeBadge({ change }: { change?: string | null }) {
  if (!change) return null;
  const color = change === "Status Improved" ? "success" : change === "Status Weakened" ? "error" : "default";
  return <Chip label={change} size="small" color={color as "success" | "error" | "default"} variant="outlined" />;
}

// Portfolio Intelligence Phase 2 (Market Signals column) — one color per
// signal `code` (a stable, backend-owned enum string; the backend never
// sends styling, same convention as `STATUS_COLORS`/`ZONE_COLORS` above).
// A code this frontend doesn't recognize yet (a future signal source added
// backend-side) still renders, just in the neutral fallback color, rather
// than being hidden.
const MARKET_SIGNAL_COLORS: Record<string, string> = {
  TOP_BREAKOUT: "#2E7D32", // success.dark -- mirrors STATUS_COLORS' breakout-leaning greens
  SHIFT_TO_BREAKOUT: "#0288D1", // info.main -- a transition event, not a steady-state status
  RE_BREAKOUT_AFTER_FAILURE: "#EF6C00", // orange -- a distinct re-breakout after a failed breakout (AUD-002)
  RSI2_PULLBACK: "#6A1B9A", // purple -- matches Rsi2PullbackPage's own SMA(200)/RSI line color
  VOLUME_OBV_BREAKOUT: "#00695C", // teal -- Volume + OBV Breakout Scanner (read-only readout)
};
const MARKET_SIGNAL_FALLBACK_COLOR = "#616161";

// Filter options for the "Market signal" dropdown -- same codes the backend
// emits (and validates server-side), in the order the sources were added.
const SIGNAL_FILTER_OPTIONS: { code: NonNullable<PortfolioSearchParams["signal"]>; label: string }[] = [
  { code: "TOP_BREAKOUT", label: "Top Breakout" },
  { code: "SHIFT_TO_BREAKOUT", label: "Shift to Breakout" },
  { code: "RE_BREAKOUT_AFTER_FAILURE", label: "Re-Breakout After Failure" },
  { code: "RSI2_PULLBACK", label: "RSI(2) Pullback" },
  { code: "VOLUME_OBV_BREAKOUT", label: "Volume + OBV Breakout" },
];

/**
 * Compact, color-coded tag list for one holding's `market_signals`
 * (requirement 1: "Market Signals column ... every applicable signal as a
 * small, color-coded tag; show 'No active signal' when none match").
 * Clicking a tag with a `view` deep-links to that signal's Market
 * Opportunities tab, pre-filled to this row's symbol via the SAME
 * `?symbol=` convention `MarketOpportunitiesPage.tsx` already reads
 * (requirement 3: multiple simultaneous tags, clickable where feasible — a
 * tag whose source page has no symbol-aware deep link, e.g. RSI(2) Pullback
 * today, still navigates to the right tab, just without a pre-filled
 * search).
 */
const MARKET_SIGNAL_ENGINE_LABELS: Record<string, string> = {
  BREAKOUT_SCORE_V2: "Global Breakout Score",
  RSI2_PULLBACK: "RSI(2) Pullback",
  VOLUME_OBV_SCANNER: "Volume + OBV Scanner",
};

function MarketSignalsCell({ symbol, signals }: { symbol: string; signals: PortfolioMarketSignal[] }) {
  const navigate = useNavigate();

  if (signals.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No active signal
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {signals.map((signal) => {
        const color = MARKET_SIGNAL_COLORS[signal.code] ?? MARKET_SIGNAL_FALLBACK_COLOR;
        const asOfLabel = signal.as_of ? new Date(signal.as_of).toLocaleString("en-IN") : null;
        const engineLabel = signal.engine ? `Engine: ${MARKET_SIGNAL_ENGINE_LABELS[signal.engine] ?? signal.engine}` : null;
        const tooltip = [engineLabel, signal.detail, asOfLabel ? `As of ${asOfLabel}` : null]
          .filter(Boolean)
          .join(" — ");
        const chip = (
          <Chip
            key={signal.code}
            label={signal.label}
            size="small"
            clickable={Boolean(signal.view)}
            onClick={
              signal.view
                ? () =>
                    navigate(
                      `/investing-idea?tab=market-opportunities&view=${encodeURIComponent(signal.view!)}&symbol=${encodeURIComponent(symbol)}`
                    )
                : undefined
            }
            sx={{ bgcolor: color, color: "#fff", fontWeight: 600 }}
          />
        );
        return tooltip ? (
          <Tooltip key={signal.code} title={tooltip}>
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      })}
    </Stack>
  );
}

// Portfolio Health radial gauge (UI review 2026-10-03) — mirrors the
// backend's own 3 bands (`_HEALTH_STRONG` = 70, `_HEALTH_MODERATE` = 40, see
// `PortfolioQueryService._build_summary`): Strong/Moderate/Needs Attention,
// colored with the SAME zone palette used everywhere else on this page
// rather than inventing a 4th color scale. A small inline SVG arc — no
// charting library needed for a single gauge.
function HealthGauge({ score, label }: { score: number | null; label: string }) {
  const color =
    label === "Strong"
      ? ZONE_COLORS["Accumulate Zone"]
      : label === "Moderate"
        ? ZONE_COLORS["Watch Zone"]
        : label === "Needs Attention"
          ? ZONE_COLORS["Review Zone"]
          : "#9E9E9E";
  const pct = score !== null ? Math.max(0, Math.min(100, score)) : 0;
  const radius = 34;
  const circumference = Math.PI * radius; // half-circle arc length
  const dash = (pct / 100) * circumference;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box component="span" sx={{ position: "relative", width: 72, height: 40, display: "inline-block" }}>
        <svg width="72" height="40" viewBox="0 0 80 44" style={{ position: "absolute", top: 0, left: 0 }}>
          <path d="M 6 40 A 34 34 0 0 1 74 40" fill="none" stroke="#E0E0E0" strokeWidth="8" strokeLinecap="round" />
          {score !== null && (
            <path
              d="M 6 40 A 34 34 0 0 1 74 40"
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circumference}`}
            />
          )}
        </svg>
      </Box>
      <Box component="span" sx={{ fontSize: "1.75rem", fontWeight: 800, color, lineHeight: 1 }}>
        {score !== null ? score : "—"}
      </Box>
    </Box>
  );
}

interface HoldingFormState {
  symbol: string;
  // Only relevant if `symbol` isn't already in the known stock list below —
  // see `StockDataImportService.get_or_create_stock` on the backend.
  exchange: "NSE" | "BSE";
  quantity: string;
  averageBuyPrice: string;
}

const EMPTY_FORM: HoldingFormState = { symbol: "", exchange: "NSE", quantity: "", averageBuyPrice: "" };

export function PortfolioTrackerPage() {
  // Investing Idea architecture finalization (2026-09-14): "View Analysis"
  // now opens the shared `StockResearchDialog` in place instead of
  // navigating away to Market Opportunities with a pre-filled search box —
  // one consistent Stock Research view, reused across every Investing Idea
  // page, no new route/page. Reuses existing APIs only, same as every
  // other entry point (see `StockResearchDialog`'s own docstring).
  const [researchSymbol, setResearchSymbol] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // `null` means "no column explicitly chosen yet" — the table then shows
  // the backend's own default (Bullish Score highest-first, then Last
  // Updated newest-first) and omits sort_by/sort_order from the API call
  // entirely, so the backend's composite tie-break stays in effect (see
  // `PortfolioQueryService._sort`). Restored from localStorage so a
  // previously-chosen sort survives a page refresh.
  const [sortBy, setSortBy] = useState<PortfolioSortField | null>(() => loadStoredSort()?.field ?? null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => loadStoredSort()?.order ?? "desc");

  const [rows, setRows] = useState<PortfolioHoldingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<PortfolioSummary>({
    total_holdings: 0,
    strong_breakout: 0,
    breakout_candidate: 0,
    developing_breakout: 0,
    no_confirmed_breakout: 0,
    accumulation_opportunities: 0,
    near_target_count: 0,
    strong_sectors_count: 0,
    attention_required_count: 0,
    portfolio_health_score: null,
    portfolio_health_label: "Not Available",
  });
  const [sectorBreakdown, setSectorBreakdown] = useState<SectorBreakdownRow[]>([]);
  const [concentration, setConcentration] = useState<PortfolioConcentration>({
    high_stock_concentration: false,
    high_sector_concentration: false,
  });
  const [performance, setPerformance] = useState<PortfolioPerformance>({
    priced_holdings_count: 0,
    total_holdings_count: 0,
    is_approximate: true,
    benchmark_available: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Which summary card is currently active — `null` means "Total Holdings"
  // (no filter, every holding shows). Clicking a status card narrows the
  // table below to just that band; clicking it again (or clicking Total
  // Holdings) clears the filter back to everything. The summary counts
  // themselves are computed by the backend over the whole portfolio
  // regardless of this filter, so they never change as the user clicks
  // between cards — only the table rows below do.
  //
  // Portfolio Intelligence Phase 1 bug fix: this used to be sent to the
  // backend as `status` (a raw `BreakoutStatus` value), a query param the
  // backend never actually read — the click had no server-side effect.
  // Now stores the equivalent Portfolio Status ZONE LABEL (e.g. "Accumulate
  // Zone") and is sent as the new, backend-supported `portfolio_status`
  // param — this one state now drives BOTH the legacy 5-item text summary
  // bar below AND the new "Accumulation Opportunities"/"Attention Required"
  // KPI cards, since both ultimately filter the same underlying field.
  const [portfolioStatusFilter, setPortfolioStatusFilter] = useState<string | null>(null);
  // Sector Intelligence click-to-filter (item 6) — independent of the
  // status/zone filter above; both can be active at once (the backend
  // applies both as an AND).
  const [sectorFilter, setSectorFilter] = useState<string | null>(null);
  // Market Signals source filter (server-side, ANDed with the two above).
  const [signalFilter, setSignalFilter] = useState<NonNullable<PortfolioSearchParams["signal"]> | null>(null);

  // The holding currently shown in the "View Details" modal, or `null`.
  const [detailRow, setDetailRow] = useState<PortfolioHoldingRow | null>(null);

  const [stockOptions, setStockOptions] = useState<StockMaster[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [form, setForm] = useState<HoldingFormState>(EMPTY_FORM);
  const [editingHoldingId, setEditingHoldingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PortfolioHoldingRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Persist the active sort so it's still applied after a page refresh.
  useEffect(() => {
    if (!sortBy) return;
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field: sortBy, order: sortOrder }));
    } catch {
      // localStorage can throw in private-browsing/storage-full edge cases —
      // sorting still works for the current session, it just won't persist.
    }
  }, [sortBy, sortOrder]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await portfolioService.list({
        page: page + 1,
        page_size: pageSize,
        portfolio_status: portfolioStatusFilter ?? undefined,
        sector: sectorFilter ?? undefined,
        signal: signalFilter ?? undefined,
        sort_by: sortBy ?? undefined,
        sort_order: sortBy ? sortOrder : undefined,
      });
      setRows(response.items);
      setTotal(response.meta.total);
      setSummary(response.summary);
      setSectorBreakdown(response.sector_breakdown);
      setConcentration(response.concentration);
      setPerformance(response.performance);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, portfolioStatusFilter, sectorFilter, signalFilter, sortBy, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  // Latest session the Volume + OBV scanner has results for (`undefined` =
  // not loaded, `null` = scanner has never produced results). Fetched only
  // when that signal filter yields an empty table, so the empty state can tell
  // "no holding carries this signal" apart from "the scanner hasn't run".
  const [scannerLatestSession, setScannerLatestSession] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (loading || rows.length > 0 || signalFilter !== "VOLUME_OBV_BREAKOUT") return;
    let cancelled = false;
    volumeObvBreakoutService
      .list({ page_size: 1 })
      .then((r) => {
        if (!cancelled) setScannerLatestSession(r.latest_scanned_date ?? null);
      })
      .catch(() => {
        if (!cancelled) setScannerLatestSession(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [loading, rows.length, signalFilter]);

  let emptyFilterMessage = "None of your holdings match this filter. Click the active card/sector again to see all holdings.";
  if (signalFilter) {
    const label = SIGNAL_FILTER_OPTIONS.find((o) => o.code === signalFilter)?.label ?? signalFilter;
    emptyFilterMessage = `None of your holdings currently carry a ${label} signal.`;
    if (signalFilter === "VOLUME_OBV_BREAKOUT" && scannerLatestSession !== undefined) {
      emptyFilterMessage +=
        scannerLatestSession === null
          ? " The scanner has not produced any results yet — an admin can run it from Data Operations."
          : ` Latest scanned session: ${scannerLatestSession.slice(8, 10)}/${scannerLatestSession.slice(5, 7)}/${scannerLatestSession.slice(2, 4)}.`;
    }
  }

  // Clicking a column header the first time sorts ascending; clicking the
  // SAME column again toggles to descending, then back to ascending, and
  // so on (requirement: "Clicking the same column repeatedly should toggle
  // between ascending and descending order"). Re-sorting always jumps back
  // to page 1 — row positions can move anywhere in the set, so staying on
  // the same page index could otherwise land on an unrelated page.
  const handleSort = (field: PortfolioSortField) => {
    setSortOrder((prevOrder) => (sortBy === field ? (prevOrder === "asc" ? "desc" : "asc") : "asc"));
    setSortBy(field);
    setPage(0);
  };

  // Purely for display: before the user has clicked any column, visually
  // highlight Breakout Score/descending as "active" since that's the
  // dominant key of the backend's actual default sort — without ever
  // setting `sortBy` itself to "breakout_score", which would drop the Last
  // Updated tie-break (see the `sortBy` state comment above).
  const activeSortField = sortBy ?? "breakout_score";
  const activeSortOrder = sortBy ? sortOrder : "desc";

  // Add Stock's autocomplete used to preload the full active list once on
  // mount — fine at 50 stocks, not at the ~9,600+ the bulk NSE/BSE catalog
  // seed added (see backend/scripts/seed_full_security_master.py). Instead,
  // search-as-you-type against the full catalog (active or not) via the
  // dedicated /stocks/search endpoint, debounced.
  useEffect(() => {
    if (!formOpen || formMode !== "add") return undefined;
    const query = form.symbol.trim();
    if (!query) {
      setStockOptions([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      stockMarketService
        .searchStocks(query, 20)
        .then((response) => setStockOptions(response.items))
        .catch(() => setStockOptions([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [form.symbol, formOpen, formMode]);

  // `status: null` (Total Holdings) means "show everything" — clicking it
  // clears whatever band filter is active. The other four each carry the
  // exact `BreakoutStatus` value the backend's `status` query param expects.
  const summaryCards = useMemo(
    () => [
      { label: "Total Holdings", value: summary.total_holdings, color: "text.primary", status: null },
      {
        label: "Strong Breakout",
        value: summary.strong_breakout,
        color: STATUS_COLORS.STRONG_BREAKOUT,
        status: "STRONG_BREAKOUT" as BreakoutStatus,
      },
      {
        label: "Breakout Candidate",
        value: summary.breakout_candidate,
        color: STATUS_COLORS.BREAKOUT_CANDIDATE,
        status: "BREAKOUT_CANDIDATE" as BreakoutStatus,
      },
      {
        label: "Developing Breakout",
        value: summary.developing_breakout,
        color: STATUS_COLORS.DEVELOPING_BREAKOUT,
        status: "DEVELOPING_BREAKOUT" as BreakoutStatus,
      },
      {
        label: "No Confirmed Breakout",
        value: summary.no_confirmed_breakout,
        color: STATUS_COLORS.NO_CONFIRMED_BREAKOUT,
        status: "NO_CONFIRMED_BREAKOUT" as BreakoutStatus,
      },
    ],
    [summary]
  );

  // Clicking the already-active card clears the filter (back to Total
  // Holdings); clicking a different card switches to it. Always resets to
  // page 1 since the filtered row set can be a completely different size.
  // Converts the clicked band into its equivalent zone label before storing
  // it (see `portfolioStatusFilter`'s own comment for why).
  const handleSummaryCardClick = (cardStatus: BreakoutStatus | null) => {
    const zoneLabel = cardStatus ? ZONE_LABEL_FOR_STATUS[cardStatus] : null;
    setPortfolioStatusFilter((prev) => (prev === zoneLabel ? null : zoneLabel));
    setPage(0);
  };

  // Portfolio Insights KPI cards' click-to-filter (item 4). "Portfolio
  // Health" and "Strong Sectors" are informational-only — there's no
  // single-field backend filter that cleanly represents either (health is a
  // portfolio-wide ratio, not a per-holding attribute; "strong sectors"
  // would need an OR across N sector names to reproduce as a table filter),
  // so per spec's own "use judgement" allowance, "Strong Sectors" instead
  // scrolls down to the Sector Intelligence section below rather than
  // filtering, and "Portfolio Health" has no click action at all.
  const toggleAccumulationFilter = () => {
    setPortfolioStatusFilter((prev) => (prev === "Accumulate Zone" ? null : "Accumulate Zone"));
    setPage(0);
  };
  const toggleAttentionFilter = () => {
    setPortfolioStatusFilter((prev) => (prev === "Review Zone" ? null : "Review Zone"));
    setPage(0);
  };
  const scrollToSectorIntelligence = () => {
    document.getElementById("sector-intelligence-section")?.scrollIntoView({ behavior: "smooth" });
  };
  const handleSectorRowClick = (sector: string) => {
    setSectorFilter((prev) => (prev === sector ? null : sector));
    setPage(0);
  };

  const openAddDialog = () => {
    setFormMode("add");
    setForm(EMPTY_FORM);
    setEditingHoldingId(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEditDialog = (row: PortfolioHoldingRow) => {
    setFormMode("edit");
    setForm({
      symbol: row.symbol,
      exchange: "NSE",
      quantity: row.quantity,
      averageBuyPrice: row.average_buy_price,
    });
    setEditingHoldingId(row.holding_id);
    setFormError(null);
    setFormOpen(true);
  };

  const closeDialog = () => {
    if (formBusy) return;
    setFormOpen(false);
  };

  const handleSubmit = async () => {
    setFormError(null);
    const quantityNum = Number.parseFloat(form.quantity);
    const priceNum = Number.parseFloat(form.averageBuyPrice);
    if (!form.symbol) {
      setFormError("Please choose a stock symbol");
      return;
    }
    if (Number.isNaN(quantityNum) || quantityNum <= 0) {
      setFormError("Quantity must be greater than zero");
      return;
    }
    if (Number.isNaN(priceNum) || priceNum <= 0) {
      setFormError("Average Buy Price must be greater than zero");
      return;
    }

    setFormBusy(true);
    try {
      if (formMode === "add") {
        await portfolioService.addHolding({
          symbol: form.symbol,
          exchange: form.exchange,
          quantity: form.quantity,
          average_buy_price: form.averageBuyPrice,
        });
      } else if (editingHoldingId) {
        await portfolioService.updateHolding(editingHoldingId, {
          quantity: form.quantity,
          average_buy_price: form.averageBuyPrice,
        });
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(extractErrorMessage(err));
    } finally {
      setFormBusy(false);
    }
  };

  // CSV export (UI review 2026-10-03) — exports exactly the rows currently
  // loaded (i.e. the active page + any active status/sector filter), using
  // the SAME formatted values shown on screen rather than raw numbers, so
  // what the user downloads matches what they were just looking at.
  const handleExportCsv = () => {
    const exportRows = rows.map((row) => ({
      Symbol: row.symbol,
      Quantity: num(row.quantity, 0),
      "Average Buy Price": num(row.average_buy_price, 0),
      "Current Price": row.current_price ? num(row.current_price, 0) : "",
      "Target Price": row.target_price ? num(row.target_price, 0) : "",
      "Target Upside %": signedPct(row.target_upside_pct),
      "Breakout Score": num(row.breakout_score, 0),
      Zone: row.portfolio_status ?? "",
      "Status Change": row.status_change ?? "",
      "Market Signals": row.market_signals.map((s) => s.label).join("; "),
      "Last Updated": row.last_updated ? new Date(row.last_updated).toLocaleString("en-IN") : "",
    }));
    exportToCsv(`portfolio-holdings-${new Date().toISOString().slice(0, 10)}.csv`, exportRows);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await portfolioService.deleteHolding(deleteTarget.holding_id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Stack direction="row" spacing={1} sx={{ mr: { xs: 0, sm: 7 } }}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export the holdings currently shown below to CSV">
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCsv}
                disabled={rows.length === 0}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
            Add Stock
          </Button>
        </Stack>
      </Box>

      {/* ---- Portfolio Insights (Portfolio Intelligence Phase 1) ---- */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryMetricCard
            icon={<FavoriteIcon fontSize="small" color="action" />}
            label="Portfolio Health"
            value={<HealthGauge score={summary.portfolio_health_score} label={summary.portfolio_health_label} />}
            interpretation={summary.portfolio_health_label}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box onClick={toggleAccumulationFilter} sx={{ cursor: "pointer" }}>
            <SummaryMetricCard
              icon={<TrendingUpIcon fontSize="small" color="action" />}
              label="Accumulation Opportunities"
              value={summary.accumulation_opportunities}
              interpretation={portfolioStatusFilter === "Accumulate Zone" ? "Filter active — click to clear" : "Click to filter"}
              accentColor={ZONE_COLORS["Accumulate Zone"]}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryMetricCard
            icon={<GpsFixedIcon fontSize="small" color="action" />}
            label="Near Target"
            value={summary.near_target_count}
            interpretation="Informational — within 5% of Indicative Research Target"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box onClick={scrollToSectorIntelligence} sx={{ cursor: "pointer" }}>
            <SummaryMetricCard
              icon={<FactCheckIcon fontSize="small" color="action" />}
              label="Strong Sectors"
              value={summary.strong_sectors_count}
              interpretation="Click to jump to Sector Intelligence"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box onClick={toggleAttentionFilter} sx={{ cursor: "pointer" }}>
            <SummaryMetricCard
              icon={<WarningAmberIcon fontSize="small" color="action" />}
              label="Attention Required"
              value={summary.attention_required_count}
              interpretation={portfolioStatusFilter === "Review Zone" ? "Filter active — click to clear" : "Click to filter"}
              accentColor={ZONE_COLORS["Review Zone"]}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ---- Summary metrics (click to filter the table below) ---- */}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="baseline"
        columnGap={1.5}
        rowGap={0.5}
        mb={1}
      >
        {summaryCards.map((card, index) => {
          const active = card.status ? portfolioStatusFilter === ZONE_LABEL_FOR_STATUS[card.status] : !portfolioStatusFilter;
          return (
            <Box key={card.label} component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 1.5 }}>
              {index > 0 && (
                <Typography component="span" variant="body2" color="text.disabled" sx={{ userSelect: "none" }}>
                  |
                </Typography>
              )}
              <Typography
                component="span"
                variant="body2"
                onClick={() => handleSummaryCardClick(card.status)}
                sx={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                  fontWeight: active ? 700 : 500,
                  borderBottom: "2px solid",
                  borderColor: active ? card.color : "transparent",
                  pb: 0.25,
                  "&:hover": { color: "text.primary" },
                }}
              >
                {card.label}: <Box component="span" sx={{ color: card.color, fontWeight: 700 }}>{card.value}</Box>
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {/* UI review (2026-10-03): the active filter(s) used to be a plain
          caption sentence naming them — now rendered as removable Chips,
          so clearing a filter doesn't require re-finding and re-clicking
          the exact card/sector row that set it. */}
      <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={1} mt={2} mb={1.5}>
      <TextField
        select
        size="small"
        label="Market signal"
        value={signalFilter ?? ""}
        onChange={(e) => {
          setSignalFilter((e.target.value || null) as typeof signalFilter);
          setPage(0);
        }}
        sx={{ minWidth: 220 }}
      >
        <MenuItem value="">All holdings</MenuItem>
        {SIGNAL_FILTER_OPTIONS.map((o) => (
          <MenuItem key={o.code} value={o.code}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      {portfolioStatusFilter || sectorFilter || signalFilter ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">
            Filtering by:
          </Typography>
          {portfolioStatusFilter && (
            <Chip
              label={portfolioStatusFilter}
              size="small"
              onDelete={() => {
                setPortfolioStatusFilter(null);
                setPage(0);
              }}
              sx={{
                bgcolor: ZONE_COLORS[portfolioStatusFilter],
                color: "#fff",
                fontWeight: 600,
                "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.8)" },
              }}
            />
          )}
          {sectorFilter && (
            <Chip
              label={sectorFilter}
              size="small"
              variant="outlined"
              onDelete={() => {
                setSectorFilter(null);
                setPage(0);
              }}
            />
          )}
          {signalFilter && (
            <Chip
              label={`Signal: ${SIGNAL_FILTER_OPTIONS.find((o) => o.code === signalFilter)?.label ?? signalFilter}`}
              size="small"
              variant="outlined"
              onDelete={() => {
                setSignalFilter(null);
                setPage(0);
              }}
            />
          )}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Click a card above (or a sector below) to filter the table to just that status.
        </Typography>
      )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 6, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {portfolioStatusFilter || sectorFilter || signalFilter ? "No matching holdings" : "Your portfolio is empty"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {portfolioStatusFilter || sectorFilter || signalFilter
              ? emptyFilterMessage
              : "Add a stock you hold to see how it compares with the Breakout Score Engine's current read."}
          </Typography>
          {portfolioStatusFilter || sectorFilter || signalFilter ? (
            <Button
              variant="outlined"
              onClick={() => {
                setPortfolioStatusFilter(null);
                setSectorFilter(null);
                setSignalFilter(null);
                setPage(0);
              }}
            >
              Show All Holdings
            </Button>
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
              Add Stock
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {SORTABLE_COLUMNS.map((column) => (
                  <Fragment key={column.field}>
                    <TableCell
                      align={column.align ?? "left"}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: column.field === "last_updated" ? "nowrap" : undefined,
                        // Stock Symbol stays pinned to the left edge while the
                        // table scrolls horizontally (it's the one column a
                        // user needs visible no matter which other columns
                        // they've scrolled to) — stacked above the row body's
                        // own sticky symbol cell (z-index 2) since this is
                        // ALSO a `stickyHeader` cell (sticky top).
                        ...(column.field === "symbol"
                          ? { position: "sticky", left: 0, zIndex: 3, bgcolor: "background.paper" }
                          : undefined),
                      }}
                      sortDirection={activeSortField === column.field ? activeSortOrder : false}
                    >
                      <TableSortLabel
                        active={activeSortField === column.field}
                        direction={activeSortField === column.field ? activeSortOrder : "asc"}
                        onClick={() => handleSort(column.field)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                    {/* "Target Price" is inserted right after "Current Price" — not
                        independently sortable server-side (see SORTABLE_COLUMNS'
                        own comment), so this is plain header text, not a
                        TableSortLabel. */}
                    {column.field === "current_price" && (
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        <Tooltip title="Indicative Research Target = Current Price + a configurable multiple of ATR(14). Informational only, never a guaranteed price.">
                          <span>Target Price</span>
                        </Tooltip>
                      </TableCell>
                    )}
                    {/* "Market Signals" is inserted right after "Status Change" —
                        not independently sortable server-side (a derived,
                        per-holding list, not a stored/joined scalar column),
                        so this is plain header text, same convention as
                        "Target Price" above. */}
                    {column.field === "status_change" && (
                      <TableCell sx={{ fontWeight: 700 }}>
                        <Tooltip title="Every applicable signal currently matched against this holding's stock from RSI(2) Oversold Pullback and the Breakout Score Engine (Top Breakout / Shift to Breakout) — read-only, never recalculated here.">
                          <span>Market Signals</span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </Fragment>
                ))}
                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                // UI review (2026-10-03): a very light tint of the row's own
                // Zone color, so the Zone can be read at a glance scanning
                // down the table, without needing to stop and read the chip
                // text in every row. Low alpha keeps all the other cell text
                // easily legible on top of it.
                const zoneLabel = row.portfolio_status ?? null;
                const zoneColor = zoneLabel ? ZONE_COLORS[zoneLabel] : undefined;
                const rowTint = zoneColor ? alpha(zoneColor, 0.06) : undefined;
                return (
                <TableRow key={row.holding_id} hover sx={rowTint ? { bgcolor: rowTint } : undefined}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      // Pinned to the left edge (see header cell above) —
                      // the sticky cell needs its OWN explicit background
                      // (matching this row's zone tint) rather than
                      // inheriting the row's `bgcolor`, since a sticky
                      // positioned cell paints over whatever's beneath it
                      // independently of its parent row.
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      bgcolor: rowTint ?? "background.paper",
                    }}
                  >
                    {row.symbol}
                  </TableCell>
                  <TableCell align="right">{num(row.quantity, 0)}</TableCell>
                  <TableCell align="right">₹{num(row.average_buy_price, 0)}</TableCell>
                  <TableCell align="right">{row.current_price ? `₹${num(row.current_price, 0)}` : "—"}</TableCell>
                  <TableCell align="right">
                    {row.target_price ? (
                      `₹${num(row.target_price, 0)} (${signedPct(row.target_upside_pct)})`
                    ) : (
                      <Tooltip title="Not enough price history to compute an Indicative Research Target for this holding yet">
                        <span>—</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{num(row.breakout_score, 0)}</TableCell>
                  <TableCell>
                    <Tooltip title={row.status ? `Underlying Status: ${STATUS_LABELS[row.status]}` : "Not scored yet"}>
                      <span>
                        <PortfolioStatusChip label={row.portfolio_status} />
                      </span>
                    </Tooltip>
                    {row.breakout_freshness && <FreshnessBadge data={row.breakout_freshness} />}
                  </TableCell>
                  <TableCell>
                    <StatusChangeBadge change={row.status_change} />
                  </TableCell>
                  <TableCell>
                    <MarketSignalsCell symbol={row.symbol} signals={row.market_signals} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.last_updated ? new Date(row.last_updated).toLocaleString("en-IN") : "—"}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title={actionLabel(row)}>
                        <IconButton size="small" aria-label={actionLabel(row)} onClick={() => setDetailRow(row)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Stock Research (general, not portfolio-specific)">
                        <IconButton
                          size="small"
                          aria-label="Stock Research"
                          onClick={() => setResearchSymbol(row.symbol)}
                        >
                          <InsightsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" aria-label="Edit" onClick={() => openEditDialog(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" aria-label="Delete" onClick={() => setDeleteTarget(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* ---- Sector Intelligence (Portfolio Intelligence Phase 1) ---- */}
      <Box id="sector-intelligence-section" mt={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Sector Intelligence
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          A simple aggregate of your own holdings' Breakout Scores, grouped by sector — informational only.
          Sectors with no classified holdings are omitted (never fabricated). Click a row to filter the table above.
        </Typography>
        {sectorBreakdown.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sector data available yet — most stocks in this catalog aren't classified, or none of your
            holdings with a classified sector have a Breakout Score yet.
          </Typography>
        ) : (
          <>
            {/* UI review (2026-10-03): a quick-glance horizontal bar per
                sector (relative Portfolio Exposure), colored by Trend
                Status — a lightweight CSS bar list rather than pulling in
                Chart.js for this, since the exact table below it already
                carries the precise figures. */}
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mb: 1.5 }}>
              <Stack spacing={1}>
                {sectorBreakdown.map((sectorRow) => {
                  const maxExposure = Math.max(...sectorBreakdown.map((s) => s.exposure_pct), 1);
                  const widthPct = Math.max((sectorRow.exposure_pct / maxExposure) * 100, 2);
                  const barColor =
                    sectorRow.trend_status === "Strong" || sectorRow.trend_status === "Improving"
                      ? ZONE_COLORS["Accumulate Zone"]
                      : sectorRow.trend_status === "Stable"
                        ? "#9E9E9E"
                        : ZONE_COLORS["Review Zone"];
                  return (
                    <Box
                      key={sectorRow.sector}
                      onClick={() => handleSectorRowClick(sectorRow.sector)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Box display="flex" justifyContent="space-between" mb={0.25}>
                        <Typography
                          variant="caption"
                          fontWeight={sectorFilter === sectorRow.sector ? 700 : 500}
                        >
                          {sectorRow.sector}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sectorRow.exposure_pct.toFixed(2)}%
                        </Typography>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: 1, bgcolor: "action.hover", overflow: "hidden" }}>
                        <Box
                          sx={{
                            height: "100%",
                            width: `${widthPct}%`,
                            bgcolor: barColor,
                            borderRadius: 1,
                            outline: sectorFilter === sectorRow.sector ? "2px solid #0F172A" : undefined,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Trend Score</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>1M Trend</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>3M Trend</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <Tooltip title="Derived from the average Breakout Score across this sector's holdings — not directly from the 1M/3M price trend shown alongside it, so the two can occasionally point in different directions.">
                      <span>Trend Status</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Portfolio Exposure</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sectorBreakdown.map((sectorRow) => (
                  <TableRow
                    key={sectorRow.sector}
                    hover
                    selected={sectorFilter === sectorRow.sector}
                    onClick={() => handleSectorRowClick(sectorRow.sector)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{sectorRow.sector}</TableCell>
                    <TableCell align="right">{sectorRow.trend_score.toFixed(1)}</TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={{ color: pctColor(sectorRow.trend_1m_pct) }}>
                        {signedPct(sectorRow.trend_1m_pct)}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={{ color: pctColor(sectorRow.trend_3m_pct) }}>
                        {signedPct(sectorRow.trend_3m_pct)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sectorRow.trend_status}
                        size="small"
                        color={
                          sectorRow.trend_status === "Strong" || sectorRow.trend_status === "Improving"
                            ? "success"
                            : sectorRow.trend_status === "Stable"
                              ? "default"
                              : "error"
                        }
                        variant={sectorRow.trend_status === "Stable" ? "outlined" : "filled"}
                      />
                    </TableCell>
                    <TableCell align="right">{sectorRow.exposure_pct.toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </Box>

      {/* ---- Portfolio Concentration (Phase 1B) ---- */}
      <Box mt={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Portfolio Concentration
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          How concentrated your priced holdings are by stock and sector — informational only, never a
          recommendation to buy, sell, or rebalance anything specific.
        </Typography>
        {concentration.top_stock_exposure_pct === null || concentration.top_stock_exposure_pct === undefined ? (
          <Typography variant="body2" color="text.secondary">
            Not enough priced holdings yet to compute concentration.
          </Typography>
        ) : (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryMetricCard
                icon={<PieChartIcon fontSize="small" color="action" />}
                label="Top Stock Exposure"
                value={`${concentration.top_stock_exposure_pct}%`}
                interpretation={concentration.top_stock_symbol ?? "—"}
                accentColor={concentration.high_stock_concentration ? ZONE_COLORS["Review Zone"] : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryMetricCard
                icon={<PieChartIcon fontSize="small" color="action" />}
                label="Top 3 Stocks Exposure"
                value={
                  concentration.top3_stock_exposure_pct !== null && concentration.top3_stock_exposure_pct !== undefined
                    ? `${concentration.top3_stock_exposure_pct}%`
                    : "—"
                }
                interpretation="Informational only"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryMetricCard
                icon={<PieChartIcon fontSize="small" color="action" />}
                label="Largest Sector Exposure"
                value={
                  concentration.largest_sector_exposure_pct !== null && concentration.largest_sector_exposure_pct !== undefined
                    ? `${concentration.largest_sector_exposure_pct}%`
                    : "—"
                }
                interpretation={concentration.largest_sector ?? "—"}
                accentColor={concentration.high_sector_concentration ? ZONE_COLORS["Review Zone"] : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryMetricCard
                icon={<PieChartIcon fontSize="small" color="action" />}
                label="Top 3 Sectors Exposure"
                value={
                  concentration.top3_sector_exposure_pct !== null && concentration.top3_sector_exposure_pct !== undefined
                    ? `${concentration.top3_sector_exposure_pct}%`
                    : "—"
                }
                interpretation="Informational only"
              />
            </Grid>
          </Grid>
        )}
        {(concentration.high_stock_concentration || concentration.high_sector_concentration) && (
          <Alert severity="warning" sx={{ mt: 1.5 }} variant="outlined">
            Review Concentration —{" "}
            {[
              concentration.high_stock_concentration
                ? `${concentration.top_stock_symbol} makes up a large share of your priced holdings`
                : null,
              concentration.high_sector_concentration
                ? `${concentration.largest_sector} makes up a large share of your sector exposure`
                : null,
            ]
              .filter(Boolean)
              .join("; ")}
            . Informational only — not a recommendation to buy, sell, or rebalance.
          </Alert>
        )}
      </Box>

      {/* ---- Portfolio Performance (Phase 1B) ---- */}
      <Box mt={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Portfolio Performance
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Approximate — assumes today's holdings and quantities were held unchanged throughout each period
          (TekInvest doesn't store a transaction/lot history). A NIFTY 50 benchmark comparison isn't shown
          because no historical index price data exists in this system.
        </Typography>
        {performance.priced_holdings_count === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Not enough priced holdings yet to compute performance.
          </Typography>
        ) : (
          <>
            <Grid container spacing={1.5} mb={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryMetricCard
                  icon={<ShowChartIcon fontSize="small" color="action" />}
                  label="Portfolio Value"
                  value={performance.portfolio_value ? `₹${num(performance.portfolio_value)}` : "—"}
                  interpretation={`${performance.priced_holdings_count}/${performance.total_holdings_count} holdings priced`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryMetricCard
                  icon={<ShowChartIcon fontSize="small" color="action" />}
                  label="Invested Capital"
                  value={performance.invested_capital ? `₹${num(performance.invested_capital)}` : "—"}
                  interpretation="Priced holdings only"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryMetricCard
                  icon={<ShowChartIcon fontSize="small" color="action" />}
                  label="Current P&L"
                  value={performance.current_pnl ? `₹${num(performance.current_pnl)}` : "—"}
                  interpretation="Priced holdings only"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryMetricCard
                  icon={<ShowChartIcon fontSize="small" color="action" />}
                  label="Return %"
                  value={signedPct(performance.return_pct)}
                  interpretation="Since your recorded average buy price"
                />
              </Grid>
            </Grid>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxWidth: 520 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Return</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { label: "1 Month", value: performance.return_1m_pct },
                    { label: "3 Months", value: performance.return_3m_pct },
                    { label: "6 Months", value: performance.return_6m_pct },
                    { label: "1 Year", value: performance.return_1y_pct },
                  ].map((period) => (
                    <TableRow key={period.label}>
                      <TableCell>{period.label}</TableCell>
                      <TableCell align="right">
                        {period.value !== null && period.value !== undefined ? (
                          <Box component="span" sx={{ color: pctColor(period.value) }}>
                            {signedPct(period.value)}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" component="span">
                            Not enough history
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      {/* ---- Add / Edit dialog ---- */}
      <Dialog open={formOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{formMode === "add" ? "Add Stock" : "Edit Stock"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Autocomplete
              disabled={formMode === "edit"}
              freeSolo
              options={stockOptions.map((s) => s.symbol)}
              value={form.symbol}
              onInputChange={(_e, value) =>
                setForm((prev) => ({ ...prev, symbol: value.toUpperCase() }))
              }
              renderOption={(props, option) => {
                const known = stockOptions.find((s) => s.symbol === option);
                return (
                  <li {...props} key={option}>
                    {known ? `${known.symbol} — ${known.company_name}` : option}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stock Symbol"
                  helperText="Pick a known stock, or type any NSE/BSE symbol — it's verified live when added"
                />
              )}
            />
            <TextField
              select
              label="Exchange"
              value={form.exchange}
              disabled={formMode === "edit" || stockOptions.some((s) => s.symbol === form.symbol)}
              onChange={(e) => setForm((prev) => ({ ...prev, exchange: e.target.value as "NSE" | "BSE" }))}
              helperText="Only used to verify a symbol that isn't already known"
              fullWidth
            >
              <MenuItem value="NSE">NSE</MenuItem>
              <MenuItem value="BSE">BSE</MenuItem>
            </TextField>
            <TextField
              label="Quantity"
              type="number"
              value={form.quantity}
              onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
              fullWidth
            />
            <TextField
              label="Average Buy Price"
              type="number"
              value={form.averageBuyPrice}
              onChange={(e) => setForm((prev) => ({ ...prev, averageBuyPrice: e.target.value }))}
              inputProps={{ min: 0, step: "any" }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={formBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={formBusy}>
            {formMode === "add" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete confirmation ---- */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => (!deleteBusy ? setDeleteTarget(null) : undefined)}>
        <DialogTitle>Remove {deleteTarget?.symbol} from your portfolio?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This only removes it from your Portfolio Tracker — it does not affect any market data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteBusy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.secondary" display="block" mt={4} sx={{ opacity: 0.8 }}>
        {DISCLAIMER}
      </Typography>

      {researchSymbol && (
        <StockResearchDialog symbol={researchSymbol} onClose={() => setResearchSymbol(null)} />
      )}

      {detailRow && (
        <PortfolioHoldingDetailDialog
          row={detailRow}
          onClose={() => setDetailRow(null)}
          onEdit={() => {
            const row = detailRow;
            setDetailRow(null);
            openEditDialog(row);
          }}
        />
      )}
    </Box>
  );
}

export default PortfolioTrackerPage;
